import getSeats from './getSeats';

/**
 * Auto-selects the first N available seats from a bus layout.
 * Excludes hallway, petFriendly, and occupied seats.
 * Used by Quick Purchase to automatically assign seats to passengers.
 * @param {Array} busLayout - 2D array representing the bus seat layout.
 * @param {string} tripSlug - Trip identifier.
 * @param {number} passengerCount - Number of seats to select.
 * @returns {Array|null} Array of seat objects or null if not enough seats available.
 */
const autoSelectSeats = (busLayout, tripSlug, passengerCount) => {
  const seats = getSeats(busLayout, tripSlug);

  const availableSeats = Object.entries(seats)
    .filter(([key]) => key !== 'categories')
    .map(([, seat]) => seat)
    .filter((seat) => seat.category !== 'hallway' && !seat.petFriendly && !seat.occupied)
    .sort((a, b) => a.index - b.index);

  if (availableSeats.length < passengerCount) {
    return null;
  }

  return availableSeats.slice(0, passengerCount);
};

/**
 * Find specific seats by number in a bus layout. Verifies each seat is available.
 * Falls back to null if any requested seat is not found or is occupied.
 * @param {Array} busLayout - 2D array representing the bus seat layout.
 * @param {string} tripSlug - Trip identifier.
 * @param {Array<string>} seatNumbers - Array of seat numbers to find.
 * @returns {Array|null} Array of seat objects or null if any seat is unavailable.
 */
export const findSeatsInLayout = (busLayout, tripSlug, seatNumbers) => {
  const seats = getSeats(busLayout, tripSlug);

  const availableSeats = Object.entries(seats)
    .filter(([key]) => key !== 'categories')
    .map(([, seat]) => seat)
    .filter((seat) => seat.category !== 'hallway' && !seat.petFriendly && !seat.occupied);

  const found = seatNumbers.map((num) => availableSeats.find((seat) => seat.number === num));

  if (found.some((seat) => !seat)) {
    return null;
  }

  return found;
};

export default autoSelectSeats;
